import type { ReactNode } from 'react';

/** Les tonalités du système. `neutral` est le défaut : une surface secondaire. */
export type BadgeTone = 'neutral' | 'brand' | 'protein' | 'carbs' | 'fat' | 'warn';

/**
 * Badge — refonte « Nourri ».
 *
 * Étiquette courte et **statique** : « Favori », « Open Food Facts », un tag
 * de recette, une macro dans une légende. Prototype : `.badge`.
 *
 * ⚠️ **La pilule interactive n'est pas ce composant.** Les filtres du
 * prototype (`.chip` : « Favoris / Recherche / Scanner ») sont des boutons, et
 * `Button` les rend déjà — `shape="pill"`, `variant="secondary"` au repos et
 * `variant="selected"` une fois choisi. Un badge ne se tape pas ; lui donner
 * l'apparence d'un contrôle inviterait à essayer.
 *
 * 🔴 **Le libellé est en `--ink`, pas dans la teinte.** Le prototype écrit du
 * `--brand-dark` sur `--brand-tint` : 3,66:1, sous le seuil de 4,5 d'un texte
 * de 11 px. Aucune teinte du système ne passe sur sa propre teinte claire — la
 * pire, les glucides, tombe à 1,77:1. L'identité est donc portée par le fond,
 * où elle est parfaitement visible, et par la pastille optionnelle.
 */
export function Badge({
  children,
  tone = 'neutral',
  dot = false,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  /**
   * Pastille de tête — prototype `.dot`, pour les légendes de macro. Purement
   * décorative : elle double ce que le libellé dit déjà, ce qui est la
   * condition pour qu'une couleur ait le droit de porter du sens.
   */
  dot?: boolean;
}) {
  const classes = ['n-badge'];
  if (tone !== 'neutral') classes.push(`n-badge--${tone}`);

  return (
    <span className={classes.join(' ')}>
      {dot && (
        <span
          className={
            tone === 'neutral' ? 'n-badge__dot' : `n-badge__dot n-badge__dot--${tone}`
          }
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
